import { Link } from "react-router-dom";

import styles from "./Breadcrumbs.module.css";
import { ChevronRightIcon } from "./icons";

export interface Crumb {
  /** Visible text. A name, never a document code (docs/notes.md). */
  label: string;
  /** Route of an ancestor. The last crumb is the current page and gets no link. */
  to?: string;
}

interface BreadcrumbsProps {
  items: Crumb[];
  /** Extra class on the nav, e.g. a page's phone-only hide. */
  className?: string;
}

/**
 * The one breadcrumb trail for ManualPage, SectionPage and the approvals
 * ReviewPage. One line: a long crumb shrinks with an ellipsis and keeps its
 * full text in `title`; the current page crumb shrinks first because the h1
 * under it repeats the name. The chevron sits inside the crumb's list item, so
 * a separator never starts a line on its own.
 */
export function Breadcrumbs({ items, className }: BreadcrumbsProps) {
  const last = items.length - 1;
  return (
    <nav className={className ? `${styles.nav} ${className}` : styles.nav} aria-label="Breadcrumb">
      <ol className={styles.list}>
        {items.map((crumb, i) => {
          const current = i === last;
          return (
            <li key={`${i}-${crumb.label}`} className={current ? styles.current : styles.item}>
              {i > 0 && <ChevronRightIcon className={styles.sep} />}
              {crumb.to && !current ? (
                <Link to={crumb.to} className={styles.link} title={crumb.label}>
                  {crumb.label}
                </Link>
              ) : (
                <span
                  className={styles.text}
                  title={crumb.label}
                  aria-current={current ? "page" : undefined}
                >
                  {crumb.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
